import { ChevronLeft } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Drawer, IconButton, useMediaQuery } from '@mui/material';
import { useState, ReactNode } from 'react';
import { useSnackbar } from 'notistack';
import { useModal } from 'mui-modal-provider';
import { useApi } from 'lib/contexts/ApplicationState';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { Post, PostWithMatchPostId } from 'lib/types';

const CONFIRM_TEXT = 'Matching posts are identical images or videos, posted on different accounts/platforms.  The content of these posts look different. Are you sure you want to match the posts?';

export type DrawerType = 'credit' | 'tag' | 'match';
export type Method = 'manual' | 'copy';
interface Props {
  drawer?: DrawerType;
  count: number;
  onCancel: () => void;
  children?: ReactNode;
  posts: (Post | PostWithMatchPostId)[];
  matchId?: string;
  left?: number;
}

export const MatchDrawer = ({
  drawer,
  count,
  onCancel,
  children,
  posts,
  matchId,
  left = -24,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const [saving, setSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const sx = isMobile ? {
    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '100%' },
  } : {
    width: '35%',
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: '35%',
      boxSizing: 'border-box',
      top: 0,
      left,
      zIndex: 50,
      position: 'absolute',
      borderTopRightRadius: '5px',
    },
  };

  const header = (
    <div className=" bg-alpineTeal text-white font-bold flex justify-between items-center pl-6">
      <div>
        Match
        {' '}
        {count}
        {' '}
        selected posts
      </div>
      <IconButton onClick={onCancel}>
        <ChevronLeft className="text-white" fontSize="large" />
      </IconButton>
    </div>
  );

  /** Determine if the posts are similar enough to be matched */
  const arePostsMatchworthy = async (newPostIds: number[]) => {
    try {
      const matchWorthy = matchId
        ? API.arePostsMatchworthy(posts[0].id, newPostIds)
        : API.arePostsMatchworthy(newPostIds[0], newPostIds.slice(1));
      return matchWorthy;
    } catch (err) {
      return false;
    }
  };

  const matchPost = async (postIds: number[]) => {
    try {
      setSaving(true);
      await API.addPostsToMatch(postIds, matchId);
      setSaving(false);
      onCancel();
      enqueueSnackbar('Post matched successfully.', { variant: 'success' });
    } catch (e) {
      setSaving(false);
      enqueueSnackbar('There was an error.  Please try again later.', { variant: 'error' });
    }
  };

  const save = async () => {
    if (posts.length <= 0) {
      return;
    }

    const postIds = posts.map((p) => p.id);
    if (postIds.length <= 0) {
      onCancel();
      return;
    }

    const matchWorthy = await arePostsMatchworthy(postIds);
    if (!matchWorthy) {
      const modal = showModal(ConfirmationModal, {
        text: CONFIRM_TEXT,
        primaryAction: 'N',
        onNo: () => {
          modal.destroy();
        },
        onYes: async () => {
          await matchPost(postIds);
          modal.destroy();
        },
      });
      return;
    }
    matchPost(postIds);
  };

  return (
    <Drawer
      sx={sx}
      variant="persistent"
      anchor="left"
      open={drawer !== undefined}
      ModalProps={{
        keepMounted: isMobile, // Better open performance on mobile.
      }}
    >
      <div>
        {header}
        <div className="text-right m-2">
          <button
            type="button"
            className="btn-primary-outlined mr-2"
            aria-label="cancel match posts"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            aria-label="match posts"
            onClick={save}
            disabled={count <= 1 || saving}
          >
            {count <= 1 ? 'Select posts to save' : 'Save'}
          </button>
        </div>
        {children}
      </div>
    </Drawer>
  );
};
